import { useState, useCallback, useRef } from "react";
import { IVideoStats } from "../types/course";

interface UseVideoStatsProps {
	lessonId?: number;
}

interface UseVideoStatsReturn {
	videoStats: IVideoStats;
	updateVideoStats: (stats: Partial<IVideoStats>) => void;
	resetVideoStats: () => void;
	visualizationId: number | null;
	setVisualizationId: (id: number) => void;
	getVideoStats: () => IVideoStats;
	getVisualizationId: () => number | null;
}

const DEFAULT_VIDEO_STATS: IVideoStats = {
	isPaused: true,
	tempoPause: 0,
	tempoAtual: 0,
	ultimoTempoConhecido: null,
};

/**
 * Hook to manage video statistics state
 * Replaces the old window.videoStats and window.visualizationID pattern
 */
export const useVideoStats = ({ lessonId }: UseVideoStatsProps = {}): UseVideoStatsReturn => {
	const [videoStats, setVideoStats] = useState<IVideoStats>(DEFAULT_VIDEO_STATS);
	const [visualizationId, setVisualizationIdState] = useState<number | null>(null);

	// Use refs to provide synchronous access to current values
	// This is needed for async callbacks that need immediate access to current state
	const videoStatsRef = useRef<IVideoStats>(DEFAULT_VIDEO_STATS);
	const visualizationIdRef = useRef<number | null>(null);

	const updateVideoStats = useCallback((stats: Partial<IVideoStats>) => {
		setVideoStats((prev) => {
			const newStats = { ...prev, ...stats };
			videoStatsRef.current = newStats;
			return newStats;
		});
	}, []);

	const resetVideoStats = useCallback(() => {
		const defaultStats = { ...DEFAULT_VIDEO_STATS };
		setVideoStats(defaultStats);
		videoStatsRef.current = defaultStats;
		setVisualizationIdState(null);
		visualizationIdRef.current = null;
	}, []);

	const setVisualizationId = useCallback((id: number) => {
		setVisualizationIdState(id);
		visualizationIdRef.current = id;
	}, []);

	// Synchronous getters for use in async callbacks
	const getVideoStats = useCallback((): IVideoStats => {
		return videoStatsRef.current;
	}, []);

	const getVisualizationId = useCallback((): number | null => {
		return visualizationIdRef.current;
	}, []);

	return {
		videoStats,
		updateVideoStats,
		resetVideoStats,
		visualizationId,
		setVisualizationId,
		getVideoStats,
		getVisualizationId,
	};
};

export type { UseVideoStatsReturn };
